

import { useCallback } from 'react';

import axios from 'axios';

import noop from 'lodash/noop';

import settings from '@src/conf';
import { getTimezone } from '@src/corelib/utils/dateUtils';
import { is401 } from '@src/corelib/utils/httpUtils';

function createApiClient() {
  const instance = axios.create(settings.CLIENTS.origin);
  instance.defaults.withCredentials = true;
  instance.defaults.headers.common['Content-Type'] = 'application/json';
  return instance;
}

const instances = {
  auth: null,
  noauth: null,
};

// Module-level ref the hook updates on every render so the once-registered 401
// interceptor always reads the current auth/on401 instead of a stale closure.
const authRef = {
  auth: null,
  on401: noop,
};


function useAPIClientBase(params) {
  const { auth, on401 = noop } = params || {};

  // Keep the shared ref pointing at the latest auth state and 401 handler.
  authRef.auth = auth;
  authRef.on401 = on401;

  if (!instances.noauth) {
    instances.noauth = createApiClient();
  }

  if (!instances.auth) {
    instances.auth = createApiClient();
    instances.auth.interceptors.response.use(undefined, (error) => {
      if (is401(error.response)) {
        const currentAuth = authRef.auth;
        const currentOn401 = authRef.on401;
        const { refresh = null } = currentAuth.credentials || {};

        if (!refresh) {
          currentAuth.unauthenticate();
          currentOn401();
          return null;
        }

        return currentAuth.refreshToken().then((credentials) => {
          error.config.headers.Authorization = `Bearer ${credentials.access}`;
          return instances.auth.request(error.config);
        })
          .catch(() => {
            currentAuth.unauthenticate();
            currentOn401();
            return null;
          });
      }

      return Promise.reject(error);
    });
  }

  const request = useCallback(
    ({
      url = '',
      method = 'GET',
      payload = null,
      contentType = 'application/json',
      useAuth = true,
      headers = {},
      options = {},
    }) => {
      let jsonOrFormData = payload;
      let instance = null;
      const dataOrParams = ['GET', 'DELETE'].includes(method)
        ? 'params'
        : 'data';

      // Build the per-request headers so concurrent in-flight requests can't
      // race on the shared axios singleton's defaults.headers.common.
      const requestHeaders = {
        'Content-Type': contentType,
        'User-Timezone': getTimezone(),
        ...headers,
      };

      if (useAuth) {
        // Set instances.auth as base
        instance = instances.auth;
        // Get access token from auth state
        const { access = null } = auth.credentials || {};
        // Access token not founded
        if (!access) {
          auth.unauthenticate();
          on401();
          return Promise.reject(new Error('AccessToken not found.'));
        }

        // Access token founded — set Authorization on this request only.
        requestHeaders.Authorization = `Bearer ${access}`;
      }
      else {
        // Set instances.noauth as base
        instance = instances.noauth;
      }

      return new Promise((resolve, reject) => {
        instance
          .request({
            ...options,
            url,
            method,
            headers: requestHeaders,
            [dataOrParams]: jsonOrFormData,
          })
          .then((response) => {
            const { data } = response;
            resolve(data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    [auth, on401],
  );

  return { request };
}

export { useAPIClientBase };
